import { useState, useMemo } from 'react'

import { AutocompleteMultiselection } from '@yes.technology/react-toolkit'

import useActions from 'modules/interaction/hooks/useActions'
import useFetchSuggestions from 'modules/interaction/hooks/useFetchSuggestions'

import ActivityWithActions from '../ActivityWithActions/ActivityWithActions'

import useMultiselectionOnSelect from './useMultiselectionOnSelect'

import { MULTISELECTION_SEPARATOR } from 'utils/constants'

const getOptions = ({ selectedOptions, suggestions }) => {
  const mappedSuggestions = suggestions.map(({ uuid, des }) => ({
    id: uuid,
    value: des
  }))

  const uniqueSelectedOptions = selectedOptions.filter(
    (selectedOption) =>
      !suggestions.find((suggestion) => suggestion.uuid === selectedOption.id)
  )

  const options = [...uniqueSelectedOptions, ...mappedSuggestions]

  return options
}

const getSelectedOptions = (value) => {
  if (!value || typeof value.id !== 'string') {
    return []
  }

  const uuids = value.id.split(MULTISELECTION_SEPARATOR)
  const descriptions = value.des.split(MULTISELECTION_SEPARATOR)

  const selectedOptions = uuids.map((uuid, index) => ({
    id: uuid,
    value: descriptions[index]
  }))

  return selectedOptions
}

const optionsToObjects = (options) => {
  if (!options || !Array.isArray(options)) {
    return []
  }
  return options.map((option) => ({
    uuid: option.id,
    des: option.value
  }))
}

const ActivityAutocompleteMultiselection = ({
  parentPath,
  filteraction,
  filteractionParams,
  value,
  onChange,
  endpoint,
  statusClass,
  isFetchingBlueprints = false,
  actions = [],
  disabled,
  ...rest
}) => {
  const [inputValue, setInputValue] = useState('')

  const selectedOptions = useMemo(() => getSelectedOptions(value), [value])

  const {
    suggestions,
    debouncedFetchSuggestions,
    resetSuggestions,
    isLoading: isLoadingSuggestions
  } = useFetchSuggestions({
    parentPath,
    filteraction,
    filteractionParams,
    endpoint
  })

  const options = useMemo(
    () => getOptions({ selectedOptions, suggestions }),
    [selectedOptions, suggestions]
  )

  const onSelect = useMultiselectionOnSelect({ selectedOptions, onChange })

  const handleOnChange = (value) => {
    if (value === '') {
      resetSuggestions()
    } else {
      debouncedFetchSuggestions(value)
    }

    setInputValue(value)
  }

  const selectedObjects = useMemo(
    () => optionsToObjects(selectedOptions),
    [selectedOptions]
  )

  const { onClickAction, isLoadingFilteraction } = useActions({
    actions,
    filteraction,
    onSelectAndLink: (values) => {
      onSelect(
        values.map((value) => ({
          id: value.uuid,
          value: value.des
        })),
        'set'
      )
    },
    selectedObjects
  })

  const isLoading = isLoadingFilteraction || isLoadingSuggestions

  return (
    <ActivityWithActions {...{ actions, onClickAction }}>
      <AutocompleteMultiselection
        {...rest}
        statusClass={isLoading ? 'processing' : statusClass}
        onChange={handleOnChange}
        value={inputValue}
        isLoadingBlueprints={isFetchingBlueprints}
        disabled={disabled}
        autoComplete='new-password'
        {...{ options, selectedOptions, onSelect }}
      />
    </ActivityWithActions>
  )
}

export default ActivityAutocompleteMultiselection
